import React, { useEffect, useRef } from "react";
import { Box, Flex, Spinner, Text, useDisclosure, useToast } from "@chakra-ui/react";

import { useLang } from "../../../Hooks";
import { ReportHeader } from "../Shared/Components/ReportHeader";
import { ReportToolbar } from "../Shared/Components/ReportToolbar";
import { reportToolbarStyles } from "../Shared/Styles/reportToolbarStyles";
import ProfitAndLossActions from "./Components/ProfitAndLossActions";
import ProfitAndLossFilters from "./Components/ProfitAndLossFilters";
import ProfitAndLossTable from "./Components/ProfitAndLossTable";
import DateRangeModal from "./Components/DateRangeModal";
import useProfitAndLossData from "./hooks/useProfitAndLossData";

export default function ProfitAndLoss() {
  const toast = useToast();
  const { texts } = useLang();
  const dateModal = useDisclosure();
  const data = useProfitAndLossData(texts, toast);
  const isRTL = texts.en === "Arabic";
  const { fetchReport } = data;
  const initialFiltersRef = useRef(data.filters);

  useEffect(() => {
    void fetchReport(initialFiltersRef.current);
  }, [fetchReport]);

  return (
    <Box
      dir={isRTL ? "rtl" : "ltr"}
      p={0}
      bg="var(--bg-primary)"
      color="var(--text-primary)"
    >
      <DateRangeModal
        isOpen={dateModal.isOpen}
        onClose={dateModal.onClose}
        onApplyDateRange={data.handleCustomDateRange}
        texts={texts}
      />

      <Flex
        {...reportToolbarStyles.headerRow}
        direction={isRTL ? "row-reverse" : "row"}
      >
        <ReportHeader
          title={texts.reports?.profitAndLoss || "Profit and Loss"}
          subtitle={texts.reports?.financialOverview || "Financial overview"}
        />
        <ReportToolbar>
          <ProfitAndLossFilters
            currentCurrency={data.filters.currency}
            currentRange={data.currentRangeLabel}
            filters={data.filters}
            onCurrencyChange={data.handleCurrencyChange}
            onSelectRange={data.handleSelectRange}
            onOpenCustomRange={dateModal.onOpen}
            onApplyFilters={data.handleApplyFilters}
            texts={texts}
          />
          <ProfitAndLossActions
            onExport={data.handleExport}
            isExporting={data.isExporting}
            texts={texts}
          />
        </ReportToolbar>
      </Flex>

      <Box minH="50vh">
        <Box width="100%" height="100%" {...reportToolbarStyles.bodyCard}>
          {data.isLoading ? (
            <Flex justify="center" align="center" height="100%">
              <Spinner size="md" />
              <Text ml={2} fontSize="sm">
                {texts.reports?.loadingReport || "Loading report..."}
              </Text>
            </Flex>
          ) : data.reportData?.rows?.length > 0 ? (
            <ProfitAndLossTable data={data.reportData} texts={texts} />
          ) : (
            <Flex justify="center" align="center" height="100%">
              <Text fontSize="sm" color="gray.500">
                {texts.reports?.noDataAvailable || "No data available for the selected filters."}
              </Text>
            </Flex>
          )}
        </Box>
      </Box>
    </Box>
  );
}
